import { Alert, Button, Card, Spin } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BrandLogo from 'assets/img/workplansiq-logo.svg'
import { takeOidcPostLoginRedirect } from 'services/auth/oidcRedirect'
import { resolvePostLoginPath } from 'services/auth/postLoginRoute'
import { ACTIVATED, SUBSCRIPTION_PENDING, hasPendingOnboarding, isMockSubscriptionOnboardingEnabled, readPendingOnboarding, savePendingOnboarding, savePricingIntentFromRedirect } from 'services/auth/onboardingStatus'
import useAuthRepo from 'services/repo/useAuthRepo'
import { getTenantMemberships, provisionTemporaryWorkspace, saveSelectedTenant } from 'services/tenant/tenantMembershipService'
import LocalStorageUtils from 'utils/LocalStorage'

export default function OidcCallback() {
    const { exchangeOidcHandoff } = useAuthRepo(false)
    const navigate = useNavigate()
    const hasStarted = useRef(false)
    const [errorMessage, setErrorMessage] = useState("")

    useEffect(() => {
        if (hasStarted.current) return
        hasStarted.current = true

        const params = new URLSearchParams(window.location.hash.substring(1))
        const handoff = params.get('handoff')

        window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`)

        const completeLogin = async () => {
            if (!handoff) {
                setErrorMessage('Google sign-in callback is missing its handoff code.')
                return
            }

            const response = await exchangeOidcHandoff(handoff)
            if (!response || !LocalStorageUtils.jwtToken.get()) {
                setErrorMessage(response?.detail || 'Google sign-in could not be completed. Please try again.')
                return
            }

            try {
                const redirectTo = takeOidcPostLoginRedirect()
                let memberships = await getTenantMemberships()
                if (memberships.length === 1) saveSelectedTenant(memberships[0])
                const selectedPlan = JSON.parse(sessionStorage.getItem('workplansiq.selectedPlan') || 'null')
                const user = LocalStorageUtils.userInfo.get() || {}
                const ownerEmail = user.email || user.emailAddress || user.preferred_username || user.username
                savePricingIntentFromRedirect(redirectTo, ownerEmail)
                if (!memberships.length && selectedPlan) {
                    savePendingOnboarding({
                        ...readPendingOnboarding(),
                        planCode: selectedPlan.code || selectedPlan.id,
                        planTitle: selectedPlan.title,
                        capacity: selectedPlan.capacity,
                        durationMonths: selectedPlan.durationMonths,
                        status: ACTIVATED,
                        activationMethod: 'GOOGLE_OIDC',
                        onboardingStatus: SUBSCRIPTION_PENDING,
                    })
                    memberships = [provisionTemporaryWorkspace({ ownerEmail, selectedPlan })]
                }
                if (!memberships.length && isMockSubscriptionOnboardingEnabled()) {
                    memberships = [provisionTemporaryWorkspace({ ownerEmail })]
                }
                const destination = resolvePostLoginPath({ redirectTo, memberships, hasPendingOnboarding: !memberships.length && hasPendingOnboarding() })
                navigate(destination, { replace: true })
            } catch {
                setErrorMessage('You are signed in, but we could not load your organization access.')
            }
        }

        completeLogin()
    }, [exchangeOidcHandoff, navigate])

    return (
        <div className="login-page oidc-callback-page">
            <Card className="oidc-callback-card">
                <img src={BrandLogo} alt="WorkPlansIQ" />
                {errorMessage ? (
                    <>
                        <Alert type="error" showIcon message="Google sign-in failed" description={errorMessage} />
                        <Button type="primary" block size="large" onClick={() => navigate('/', { replace: true })}>
                            Back to sign in
                        </Button>
                    </>
                ) : (
                    <div className="oidc-callback-loading">
                        <Spin size="large" />
                        <h2>Completing your sign-in</h2>
                        <p>Please wait while we securely connect your WorkPlansIQ account.</p>
                    </div>
                )}
            </Card>
        </div>
    )
}
